import React, { useRef, useState } from 'react';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Select, {
    GroupBase,
    MultiValue,
    SelectInstance,
    SingleValue,
    ClearIndicatorProps,
} from 'react-select';
import { OptionProp } from '~/models/Commons ';
import { fontWeightClass } from '~/assets/styles/classes/InputClasses';

interface SelectProps {
    label: string;
    id: string;
    isMulti?: boolean;
    value?: OptionProp | OptionProp[];
    fontWeight?: 'bold' | 'normal' | 'extrabold';
    onChange: (param: OptionProp | OptionProp[] | null) => void;
    options: OptionProp[];
    hideLabel?: boolean;
    errorLabel?: string;
    isDisabled?: boolean;
    hideErrorLabel?: boolean;
    menuPortal?: boolean;
    isRequired?: boolean;
    placeholder?: string;
}

export const SIMPLSelectDropdown: React.FC<SelectProps> = ({
    id,
    isMulti = false,
    onChange,
    options,
    value,
    errorLabel = '',
    isDisabled = false,
    hideErrorLabel = false,
    label,
    hideLabel,
    menuPortal = false,
    placeholder,
    isRequired,
    fontWeight = 'bold'
}) => {
    const selectRef = useRef<SelectInstance<OptionProp, true, GroupBase<OptionProp>> | null>(null);
    const [focus, setFocus] = useState(false);

    const handleSelectChange = (
        selectedOption: SingleValue<OptionProp> | MultiValue<OptionProp> | null
    ) => {
        if (Array.isArray(selectedOption)) {
            onChange(selectedOption.length > 0 ? selectedOption : null);
        } else if (selectedOption && 'value' in selectedOption) {
            onChange(selectedOption);
        } else {
            // Handle clear
            onChange(null);
        }

        selectRef?.current?.blur();
        setFocus(false);
    };

    const getBoxShadow = (focus: boolean, errorLabel: string) => {
        if (!focus) return 'none';
        return errorLabel ? '0 0 0 0px red' : '0 0 0 1px #9B0D78';
    };

    const hasSingleValue = Array.isArray(value) && value.length > 0 && value[0]?.label !== '';
    const hasMultipleValue = Array.isArray(value) && value.length > 1;
    const singleObjectValue = !Array.isArray(value) && value !== null && value?.label !== '';

    const hasValue = Array.isArray(value)
        ? value.length > 0
        : hasSingleValue || hasMultipleValue || singleObjectValue;

    const customStyles = {
        control: (provided: any) => ({
            ...provided,
            width: 'auto',
            minHeight: '44px',
            backgroundColor: '#F5F5F5',
            borderColor: errorLabel ? 'red' : '#B0B3BA',
            outline: 'none',
            '&:hover': {
                borderColor: 'none',
            },
            boxShadow: getBoxShadow(focus, errorLabel)
        }),
        option: (provided: any) => ({
            ...provided,
            color: '#6B6F7B',
            fontSize: '0.875rem',
            backgroundColor: '#F5F5F5',
            '&:hover': {
                backgroundColor: '#CFE0FC',
            },
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: errorLabel ? '#CA3532' : '#6B6F7B',
        }),
    };

    const CustomClearIndicator = (props: ClearIndicatorProps<OptionProp, boolean>) => {
        const { innerProps } = props;
        return (
            <div {...innerProps} className="cursor-pointer">
                <HighlightOffRoundedIcon fontSize="small" />
            </div>
        );
    };

    return (
        <div>
            {!hideLabel && (
                <label
                    htmlFor={id}
                    className={`inline-block cursor-pointer text-sm ${fontWeightClass(fontWeight)} ${errorLabel ? 'text-error' : ''}`}
                >
                    {isRequired && <span className="text-red-500">*</span>} {label}
                </label>
            )}
            <div className="w-full relative">
                <Select
                    id={id}
                    menuPlacement="auto"
                    isDisabled={isDisabled}
                    className={`select-container ${errorLabel ? 'placeholder:text-[#CA3532]' : ''}`}
                    placeholder={placeholder}
                    ref={selectRef}
                    styles={customStyles}
                    onChange={handleSelectChange}
                    options={options}
                    menuIsOpen={focus}
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                    components={{
                        IndicatorSeparator: () => null,
                        ClearIndicator: CustomClearIndicator
                    }}
                    isMulti={isMulti}
                    isClearable={true}
                    data-testid="select-input"
                    value={hasValue || isMulti ? value : null}
                    menuPortalTarget={menuPortal ? document.body : null}
                />
                {!hideErrorLabel && errorLabel && (
                    <p className="pt-1 mb-4 text-error text-sm">{errorLabel}</p>
                )}
            </div>
        </div>
    );
};
